import React, { forwardRef, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { LuzmoDashboard } from "@luzmo/embed";
import { LuzmoDashboardComponent } from "@luzmo/react-embed";

import CumulAuth from "@mapmycustomers/shared/types/cumul/CumulAuth";
import DashboardType from "@mapmycustomers/shared/types/cumul/CumulDashboard";

import { CumulDashboardViewMode } from "@app/scene/reports/enums/CumulDashboardViewMode";
import { getCumulAuth } from "@app/store/cumul";
import { fetchDashboardList } from "@app/store/cumul/actions";
import { RootState } from "@app/store/rootReducer";
import useInnerRef from "@app/util/hook/useInnerRef";

import styles from "./Dashboard.module.scss";

const APP_SERVER = "https://app.us.cumul.io/";

export interface LuzmoDashboardRef extends Omit<LuzmoDashboard, "children"> {}

interface Props {
  cumulAuth?: CumulAuth;
  dashboardId: string;
  dashboardList?: Array<DashboardType>;
  fetchDashboardList: typeof fetchDashboardList.request;
  viewMode: CumulDashboardViewMode;
}

const Dashboard = forwardRef<LuzmoDashboardRef, Props>(
  ({ cumulAuth, dashboardId, fetchDashboardList, viewMode }, ref) => {
    const [innerRef, refHandler] = useInnerRef<LuzmoDashboardRef>(ref);

    useEffect(() => {
      if (viewMode === CumulDashboardViewMode.VIEW) {
        fetchDashboardList();
      }
      innerRef.current?.setEditMode(viewMode);
    }, [fetchDashboardList, innerRef, viewMode]);

    const handleLoad = useCallback(() => {
      if (innerRef.current?.editMode === viewMode) {
        return;
      } else {
        innerRef.current?.setEditMode(viewMode);
      }
    }, [innerRef, viewMode]);

    return (
      <div className={styles.dashboard}>
        {cumulAuth && (
          <LuzmoDashboardComponent
            appServer={APP_SERVER}
            authKey={cumulAuth.id}
            authToken={cumulAuth.token}
            dashboardId={`${dashboardId}?qe_version=2`}
            load={handleLoad}
            loaderSpinnerBackground="rgb(236 248 255)"
            loaderSpinnerColor="rgb(0, 81, 126)"
            ref={refHandler}
          />
        )}
      </div>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  cumulAuth: getCumulAuth(state),
});

const mapDispatchToProps = {
  fetchDashboardList: fetchDashboardList.request,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(Dashboard);
